import { useState } from 'react';
import { Button, TextField, Typography, Box } from '@mui/material';
import theme from 'theme';
import _ from 'lodash';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  Portlet,
  PortletToolbar,
  PortletContent,
  PortletFooter,
} from '../../../components/Portlets';
import { getNumberOfWorkingDays } from './getNumberOfWorkingDays';
import { VACATION_DESCRIPTION_MAX } from 'constants/fieldLimits';

const VacationRequestForm = ({
  formProps: {
    values,
    errors,
    isValid,
    handleChange,
    handleBlur,
    touched,
    dirty,
    setFieldValue,
    submitForm,
    handleReset,
  },
  tenantLoaded,
}) => {
  const [numberOfWorkingDays, setNumberOfWorkingDays] = useState('');

  const onSubmitButtonClick = async () => {
    await submitForm();
  };

  const onStartDateChange = (startDate, endDate) => {
    setFieldValue('startDate', startDate);
    if (!endDate) {
      endDate = startDate;
      setFieldValue('endDate', endDate);

      const workingDays = getNumberOfWorkingDays(new Date(startDate), new Date(endDate));
      setNumberOfWorkingDays(workingDays > 0 ? workingDays : '');
      setFieldValue('workingDays', workingDays);
    } else if (endDate < startDate) {
      endDate = startDate;
      setFieldValue('endDate', endDate);
      const workingDays = getNumberOfWorkingDays(new Date(startDate), new Date(endDate));
      setNumberOfWorkingDays(workingDays > 0 ? workingDays : '');
      setFieldValue('workingDays', workingDays);
    } else {
      const workingDays = getNumberOfWorkingDays(new Date(startDate), new Date(endDate));
      setNumberOfWorkingDays(workingDays);
      setFieldValue('workingDays', workingDays);
    }
  };

  const onEndDateChange = (startDate, endDate) => {
    setFieldValue('endDate', endDate);

    if (endDate < startDate) {
      startDate = endDate;
      setFieldValue('startDate', startDate);
    }

    const workingDays = getNumberOfWorkingDays(new Date(startDate), new Date(endDate));
    setNumberOfWorkingDays(workingDays);
    setFieldValue('workingDays', workingDays);
  };

  const onWorkingDaysChange = e => {
    // prevent inserting higher number than calculated
    if (numberOfWorkingDays > 0 && e.target.value > numberOfWorkingDays) {
      return 0;
    }
    handleChange(e);
  };

  return (
    <Portlet
      sx={{ padding: theme.spacing(0), borderRadius: '0 0 0.25rem 0.25rem' }}
      outlined={1}
      squared={false}
    >
      <PortletContent
        sx={{
          padding: '0.5rem 1.5rem',
          background: 'transparent',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Typography
          variant="body2"
          component="span"
          sx={{
            fontWeight: 600,
            marginBottom: theme.spacing(2),
            marginTop: theme.spacing(2),
            fontSize: '1rem',
          }}
        >
          Vacation Request
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <div>
            <DatePicker
              label="Start Date (inclusive)"
              value={values.startDate ?? null}
              onChange={value => onStartDateChange(value, values.endDate)}
              format="dd.MM.yyyy"
              slotProps={{
                textField: {
                  fullWidth: true,
                  variant: 'standard',
                  margin: 'normal',
                  id: 'start-date',
                  name: 'startDate',
                  error: touched.startDate && Boolean(errors.startDate),
                  helperText: errors.startDate && touched.startDate ? errors.startDate : '',
                  onBlur: handleBlur,
                  onKeyDown: e => e.preventDefault(),
                  slotProps: {
                    inputLabel: { shrink: true },
                  },
                },
                htmlInput: {
                  'data-cy': 'start-date-input',
                },
                popper: { 'data-cy': 'popover' },
                openPickerButton: {
                  'aria-label': 'change start date',
                  'data-cy': 'startDateIcon',
                },
                nextIconButton: { 'data-cy': 'startNextMonthSwitch' },
              }}
            />
          </div>
          <div>
            <DatePicker
              label="End Date (inclusive)"
              value={values.endDate ?? null}
              onChange={value => onEndDateChange(values.startDate, value)}
              format="dd.MM.yyyy"
              slotProps={{
                textField: {
                  fullWidth: true,
                  variant: 'standard',
                  margin: 'normal',
                  id: 'end-date',
                  name: 'endDate',
                  error: touched.endDate && Boolean(errors.endDate),
                  helperText: touched.endDate && errors.endDate ? errors.endDate : '',
                  onBlur: handleBlur,
                  onKeyDown: e => e.preventDefault(),
                  slotProps: {
                    inputLabel: { shrink: true },
                  },
                },
                htmlInput: {
                  'data-cy': 'end-date-input',
                },
                popper: { 'data-cy': 'popover' },
                openPickerButton: {
                  'aria-label': 'change end date',
                  'data-cy': 'endDateIcon',
                },
                nextIconButton: { 'data-cy': 'endNextMonthSwitch' },
              }}
            />
          </div>
          <div>
            <TextField
              fullWidth
              id="working-days"
              label="Number Of Working Days"
              name="workingDays"
              type="number"
              placeholder="It will be calculated automatically skipping weekends"
              value={values.workingDays || ''}
              onChange={onWorkingDaysChange}
              margin="normal"
              variant="standard"
              error={touched.workingDays && Boolean(errors.workingDays)}
              onBlur={handleBlur}
              helperText={errors.workingDays && touched.workingDays ? errors.workingDays : ''}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </div>
          <Box component="div" sx={{ marginBottom: theme.spacing(3) }}>
            <TextField
              fullWidth
              id="description"
              label="Description"
              name="description"
              variant="standard"
              placeholder="Is the team or client informed?"
              multiline
              maxRows="4"
              value={values.description || ''}
              onChange={handleChange}
              margin="normal"
              error={touched.description && Boolean(errors.description)}
              onBlur={handleBlur}
              helperText={
                (values.description || '').length >= VACATION_DESCRIPTION_MAX
                  ? `Maximum length of ${VACATION_DESCRIPTION_MAX} characters reached.`
                  : errors.description && touched.description
                    ? errors.description
                    : ''
              }
              slotProps={{
                inputLabel: { shrink: true },
                htmlInput: { maxLength: VACATION_DESCRIPTION_MAX },
              }}
            />
          </Box>
        </LocalizationProvider>
      </PortletContent>
      <PortletFooter sx={{ borderTop: 'none', marginTop: '20%' }}>
        <PortletToolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            disabled={!dirty}
            onClick={handleReset}
            variant="contained"
            sx={{
              bgcolor: 'primary.contrastText',
              color: 'common.black',
              '&:hover': { bgcolor: 'common.neutral' },
            }}
          >
            Reset
          </Button>
          <div>
            <Button
              disabled={
                !tenantLoaded ||
                _.isNil(values.endDate) ||
                _.isNil(values.startDate) ||
                _.isNil(values.workingDays) ||
                _.isNil(values.description) ||
                !isValid
              }
              variant="contained"
              sx={{
                bgcolor: 'primary.main',
                '&:hover': { bgcolor: 'primary.dark' },
              }}
              name="submit"
              onClick={onSubmitButtonClick}
              data-cy="vacationSubmit"
            >
              Submit
            </Button>
          </div>
        </PortletToolbar>
      </PortletFooter>
    </Portlet>
  );
};

export default VacationRequestForm;
